import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { UsersRound, Plus, Check } from "lucide-react";
import { motion } from "framer-motion";

const NICHES = ["affiliate", "dropshipping", "freelance", "crypto", "content", "health", "ai"];

export default function Communities() {
  const [communities, setCommunities] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", niche: "affiliate" });

  const load = async () => {
    const { data } = await api.get("/communities");
    setCommunities(data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name.trim()) return;
    await api.post("/communities", form);
    setForm({ name: "", description: "", niche: "affiliate" });
    setCreating(false);
    load();
  };

  const join = async (id) => {
    await api.post(`/communities/${id}/join`);
    load();
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto py-8 px-4 md:px-8">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-3">
              <UsersRound size={22} className="text-[#00E676]" strokeWidth={2} />
              <h1 className="font-heading font-bold text-4xl tracking-tight">Communities</h1>
            </div>
            <p className="text-sm text-white/45 font-mono mt-1">Find your tribe by niche. Trade tactics in private.</p>
          </div>
          <button
            data-testid="create-community-btn"
            onClick={() => setCreating(!creating)}
            className="cta-green rounded-md px-4 py-2 text-sm font-semibold flex items-center gap-1.5"
          >
            <Plus size={14} strokeWidth={2.5} /> {creating ? "Cancel" : "New community"}
          </button>
        </div>

        {creating && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="surface rounded-xl p-5 mb-6 space-y-3">
            <input
              data-testid="new-comm-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Community name"
              className="w-full bg-white/[0.04] border border-white/8 rounded-md px-3 py-2 focus:outline-none focus:border-[#00E676]/40"
            />
            <textarea
              data-testid="new-comm-desc"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="What this community is about"
              rows={2}
              className="w-full bg-white/[0.04] border border-white/8 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#00E676]/40"
            />
            <select
              data-testid="new-comm-niche"
              value={form.niche}
              onChange={(e) => setForm({ ...form, niche: e.target.value })}
              className="bg-white/[0.04] border border-white/8 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#00E676]/40"
            >
              {NICHES.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <button data-testid="new-comm-create" onClick={create} className="cta-green rounded-md px-5 py-2 text-sm font-bold">
              Launch
            </button>
          </motion.div>
        )}

        {communities.length === 0 ? (
          <div className="surface rounded-xl p-12 text-center">
            <UsersRound size={32} className="text-[#00E676] mx-auto mb-3" strokeWidth={1.5} />
            <div className="font-heading font-bold text-xl mb-1">No communities yet.</div>
            <div className="text-sm text-white/50">Start the first one.</div>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {communities.map((c, i) => (
              <motion.div
                key={c.community_id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                className="surface rounded-xl p-5 card-hover"
                data-testid={`community-${c.community_id}`}
              >
                <div className="flex items-center justify-between mb-3">
                  {c.niche && <span className="verified-pill px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider">{c.niche}</span>}
                  <span className="text-[10px] font-mono text-white/40">{c.members_count} member{c.members_count !== 1 && "s"}</span>
                </div>
                <h3 className="font-heading font-bold text-lg mb-1.5">{c.name}</h3>
                <p className="text-sm text-white/55 mb-4 line-clamp-2 leading-relaxed">{c.description || "—"}</p>
                <button
                  data-testid={`join-${c.community_id}`}
                  onClick={() => join(c.community_id)}
                  className={`text-xs font-bold px-3 py-1.5 rounded-md transition w-full ${
                    c.joined ? "surface-elev text-[#00E676]" : "cta-green"
                  }`}
                >
                  {c.joined ? <><Check size={12} strokeWidth={2.5} className="inline mr-1" /> Joined</> : "Join"}
                </button>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
